import type { InputHTMLAttributes } from 'react';

/** The role of a form field — drives the correct mobile keyboard + autofill. */
export type FieldKind =
  | 'amount'
  | 'phone'
  | 'postcode'
  | 'email'
  | 'name'
  | 'given-name'
  | 'additional-name'
  | 'family-name'
  | 'street-address'
  | 'address-level2'
  | 'address-level3'
  | 'government-id'
  | 'otp'
  | 'text';

type FieldAttrs = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'inputMode' | 'autoComplete'
>;

/**
 * The right keyboard + autofill for a field, by role. Spread onto an `<input>` so
 * the correct on-screen keyboard appears on a phone and the browser can autofill.
 * Correct-by-construction — part of the CCAANZ mobile standard.
 *
 *   <input {...fieldProps('email')} value={email} onChange={…} />
 *
 * `{ proxy: true }` is for a field filled by someone OTHER than the person it
 * describes — the registrar transcribing an applicant's paper form. The keyboard
 * stays; autofill goes off (`autocomplete="off"`). A personal token there would
 * offer the TYPIST's own saved name, email or address (the wrong person's data,
 * one tap from being submitted), and invite the browser to keep the subject's
 * details in the typist's form history. Without the option every call is exactly
 * what it was.
 *
 *   <input {...fieldProps('email', { proxy: isPaper })} value={email} onChange={…} />
 */
export function fieldProps(
  kind: FieldKind,
  { proxy = false }: { proxy?: boolean } = {},
): FieldAttrs {
  const attrs = attrsFor(kind);
  return proxy ? { ...attrs, autoComplete: 'off' } : attrs;
}

function attrsFor(kind: FieldKind): FieldAttrs {
  switch (kind) {
    case 'amount':
      // type=text (not number) to avoid the spinner + locale/format quirks; the
      // decimal inputMode still gives the numeric keypad.
      return { type: 'text', inputMode: 'decimal', autoComplete: 'off' };
    case 'phone':
      return { type: 'tel', inputMode: 'tel', autoComplete: 'tel' };
    case 'postcode':
      return { type: 'text', inputMode: 'numeric', autoComplete: 'postal-code' };
    case 'email':
      return { type: 'email', inputMode: 'email', autoComplete: 'email' };
    case 'name':
      return { type: 'text', autoComplete: 'name' };
    case 'given-name':
      return { type: 'text', autoComplete: 'given-name' };
    case 'additional-name':
      // Middle name(s).
      return { type: 'text', autoComplete: 'additional-name' };
    case 'family-name':
      return { type: 'text', autoComplete: 'family-name' };
    case 'street-address':
      return { type: 'text', autoComplete: 'street-address' };
    case 'address-level2':
      // The town or city.
      return { type: 'text', autoComplete: 'address-level2' };
    case 'address-level3':
      // The suburb, below the town or city in an NZ address.
      return { type: 'text', autoComplete: 'address-level3' };
    case 'government-id':
      // A government-issued identifier — an NZ driver licence or passport number.
      // It belongs to ONE person and is never legitimately reused for another, so
      // autofill has nothing right to offer; and browser form history on a shared
      // device (a parish office PC, a family tablet) would offer one person's
      // number to the next person at the keyboard. Off, whoever is typing. A plain
      // text keyboard: an NZ licence number is letters then digits.
      return { type: 'text', autoComplete: 'off' };
    case 'otp':
      // one-time-code lets iOS/Android auto-fill the SMS/email login code.
      return { type: 'text', inputMode: 'numeric', autoComplete: 'one-time-code' };
    default:
      return { type: 'text' };
  }
}
